import type { ColumnDef } from "@tanstack/react-table";
import { MultiLineTextCell } from "@/components/shared/cells/multi-line-text-input-cell";
import { SelectCell } from "@/components/shared/cells/select";
import { TextInputCell } from "@/components/shared/cells/text-input-cell";
import { useCostCentersDataContext } from "./cost-centers-data-provider";
import { CostCenterNo } from "./headers/cost-center-no";
import { DescriptionHeader } from "./headers/description";
import { NameHeader } from "./headers/name";
import { SelectHeader } from "./headers/select";

const selectColumn: ColumnDef<any> = {
  id: "select",
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => <SelectCell {...cell} />,
  enableSorting: false,
  enableHiding: false,
  size: 40,
  minSize: 40,
};

const costCenterNoColumn: () => ColumnDef<any> = () => ({
  accessorKey: "identifier",
  id: "identifier",
  size: 180,
  minSize: 180,
  enableResizing: false,
  header: (header) => <CostCenterNo {...header} />,
  sortingFn: (a, b) => {
    const aStr = a.original.identifier ?? "";
    const bStr = b.original.identifier ?? "";
    return aStr.localeCompare(bStr);
  },

  cell: (cell) => {
    const { updateCostCenters } = useCostCentersDataContext();
    const value = cell.row.original.identifier;
    const costCenterId = cell.row.original.costCenterId;

    const saveHandler = async (value: string) => {
      await updateCostCenters({
        variables: {
          costCenterIds: [cell.row.original.costCenterId],
          updatedFields: {
            identifier: value,
          },
        },
      });
    };
    return (
      <TextInputCell
        value={value}
        key={costCenterId}
        inputKey={costCenterId}
        saveHandler={saveHandler}
        className="h-[20px] ml-0 pl-0"
        allowedCharsRegex={/^[a-zA-Z0-9 ]+$/}
      />
    );
  },
});

const nameColumn: () => ColumnDef<any> = () => ({
  accessorKey: "name",
  id: "name",
  size: 500,
  minSize: 500,
  enableResizing: false,
  header: (header) => <NameHeader {...header} />,
  sortingFn: (a, b) => a.original.name.localeCompare(b.original.name),

  cell: (cell) => {
    const { updateCostCenters } = useCostCentersDataContext();
    const value = cell.row.original.name;
    const costCenterId = cell.row.original.costCenterId;
    const saveHandler = async (value: string) => {
      await updateCostCenters({
        variables: {
          costCenterIds: [cell.row.original.costCenterId],
          updatedFields: {
            name: value,
          },
        },
      });
    };
    return (
      <TextInputCell
        key={costCenterId}
        inputKey={costCenterId}
        value={value}
        saveHandler={saveHandler}
        className="h-[20px] ml-0 pl-0"
      />
    );
  },
});

const descriptionColumn: () => ColumnDef<any> = () => ({
  accessorKey: "description",
  id: "description",
  size: 1500,
  minSize: 1500,
  enableResizing: false,
  header: (header) => <DescriptionHeader {...header} />,
  enableSorting: false,
  cell: (cell) => {
    const { costCenters, updateCostCenters } = useCostCentersDataContext();
    const costCenterId = cell.row.original.costCenterId;
    const value = cell.row.original.description;

    const descriptionSaveHandler = async (value: string) => {
      await updateCostCenters({
        variables: {
          costCenterIds: [cell.row.original.costCenterId],
          updatedFields: {
            description: value,
          },
        },
      });
    };

    return (
      <MultiLineTextCell
        key={costCenterId}
        value={value}
        saveHandler={descriptionSaveHandler}
        classNameInput="h-[20px] "
      />
    );
  },
});

export const columns: () => ColumnDef<any>[] = () => [
  selectColumn,
  costCenterNoColumn(),
  nameColumn(),
  descriptionColumn(),
];
